import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { ShopType, StockStatusKey, TaskStatusKey } from "@easyshop/shared";
import { SHOP_TYPE_LABELS, TASK_STATUS_LABELS } from "@easyshop/shared";
import { useClickOutside } from "@/hooks/useClickOutside";

export function cx(...c: (string | false | null | undefined)[]) {
  return c.filter(Boolean).join(" ");
}

// --- Conteneurs -----------------------------------------------------------------

export function Card({
  children,
  className,
  pad = "px-[26px] py-[22px]",
  onClick,
  style,
}: {
  children: ReactNode;
  className?: string;
  pad?: string;
  onClick?: () => void;
  style?: CSSProperties;
}) {
  return (
    <div
      onClick={onClick}
      style={style}
      className={cx("rounded-card border border-line bg-white", pad, onClick && "cursor-pointer", className)}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("text-[13.5px] font-bold", className)}>{children}</div>;
}

/** Carte KPI : libellé gris, grand chiffre Archivo, contenu libre dessous. */
export function StatCard({
  label,
  value,
  children,
  size = "lg",
  className,
  valueClassName,
}: {
  label: ReactNode;
  value: ReactNode;
  children?: ReactNode;
  size?: "xl" | "lg" | "md" | "sm";
  className?: string;
  valueClassName?: string;
}) {
  const sizes = { xl: "text-[40px] font-extrabold tracking-[-0.01em]", lg: "text-[30px] font-bold", md: "text-[28px] font-extrabold", sm: "text-[26px] font-extrabold" };
  return (
    <Card className={cx("min-w-[140px]", className)} pad={size === "xl" || size === "lg" ? "px-[26px] py-[22px]" : "px-6 py-5"}>
      <div className="text-[12.5px] font-semibold text-muted">{label}</div>
      <div className={cx("mt-1 font-display tabular", sizes[size], valueClassName)}>{value}</div>
      {children}
    </Card>
  );
}

// --- Badges ---------------------------------------------------------------------

export function Pill({ children, className, style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <span style={style} className={cx("inline-block whitespace-nowrap rounded-full px-[9px] py-[3px] text-[10.5px] font-bold", className)}>
      {children}
    </span>
  );
}

export function TypeBadge({ type, synced = true, className }: { type: ShopType; synced?: boolean; className?: string }) {
  if (!synced) return <Pill className={cx("bg-warn-bg text-warn", className)}>Non synchronisée</Pill>;
  return (
    <Pill className={cx(type === "INTEGRATED" ? "bg-positive-bg text-positive" : "bg-franchise-bg text-franchise", className)}>
      {SHOP_TYPE_LABELS[type]}
    </Pill>
  );
}

const STOCK_STYLE: Record<StockStatusKey, [string, string]> = {
  rupture: ["bg-danger-bg text-danger", "Rupture"],
  surveiller: ["bg-warn-bg text-warn", "À surveiller"],
  ok: ["bg-positive-bg text-positive", "OK"],
};

export function StockBadge({ status, className }: { status: StockStatusKey; className?: string }) {
  const [c, label] = STOCK_STYLE[status];
  return <Pill className={cx(c, className)}>{label}</Pill>;
}

export function stockDotClass(status: StockStatusKey) {
  return status === "rupture" ? "bg-danger" : status === "surveiller" ? "bg-warn" : "bg-positive";
}

export function taskStatusClass(status: TaskStatusKey, late = false) {
  if (status === "DONE" || status === "VALIDATED") return "bg-positive-bg text-positive";
  if (status === "REFUSED" || late) return "bg-danger-bg text-danger";
  return "bg-warn-bg text-warn";
}

export function TaskStatusBadge({ status, late, className }: { status: TaskStatusKey; late?: boolean; className?: string }) {
  return <Pill className={cx(taskStatusClass(status, late), "text-[11px]", className)}>{TASK_STATUS_LABELS[status]}</Pill>;
}

export function DeltaPill({ delta, className }: { delta: number | null; className?: string }) {
  if (delta == null) return <Pill className={cx("bg-[oklch(0.94_0.008_150)] text-[oklch(0.65_0.01_150)] text-[12.5px] px-[10px]", className)}>—</Pill>;
  const up = delta >= 0;
  return (
    <Pill className={cx(up ? "bg-positive-bg text-positive" : "bg-danger-bg text-danger", "px-[10px] text-[12.5px]", className)}>
      {up ? "▲" : "▼"} {Math.abs(delta).toFixed(1).replace(".", ",")} %
    </Pill>
  );
}

// --- Barres ------------------------------------------------------------------------

export function Bar({ pct, color = "bg-accent", height = "h-[6px]", className, track = "bg-track" }: { pct: number; color?: string; height?: string; className?: string; track?: string }) {
  return (
    <div className={cx("overflow-hidden rounded-[3px]", height, track, className)}>
      <div className={cx("h-full", color)} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

// --- Courbe CA -----------------------------------------------------------------------

function toPath(values: number[], max: number, slots = values.length): string {
  if (values.length === 0) return "";
  return values
    .map((v, i) => {
      const x = slots > 1 ? Math.round((i / (slots - 1)) * 1000) : 0;
      const y = Math.round(228 - (max > 0 ? v / max : 0) * 210);
      return `${i === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");
}

/** Courbe du design (viewBox 1000×230) : aire + ligne courante + ligne précédente en pointillés. */
export function RevenueChart({
  current,
  previous,
  height = 210,
  axis,
}: {
  current: number[];
  previous?: number[];
  height?: number;
  axis?: string[];
}) {
  const max = Math.max(1, ...current, ...(previous ?? [])) * 1.08;
  // Période en cours : la ligne courante est plus courte que la précédente et
  // s'arrête à l'instant présent (même échelle horizontale que l'axe).
  const slots = Math.max(current.length, previous?.length ?? 0);
  const line = toPath(current, max, slots);
  const lastX = slots > 1 ? Math.round(((current.length - 1) / (slots - 1)) * 1000) : 1000;
  const area = line ? `${line} L ${lastX},228 L 0,228 Z` : "";
  return (
    <div>
      <svg viewBox="0 0 1000 230" preserveAspectRatio="none" className="mt-3 w-full" style={{ height }}>
        {area && <path d={area} fill="oklch(0.5 0.13 152 / 0.1)" stroke="none" />}
        {previous && previous.length > 0 && (
          <path d={toPath(previous, max, slots)} fill="none" stroke="oklch(0.8 0.01 150)" strokeWidth={2} strokeDasharray="6 5" vectorEffect="non-scaling-stroke" />
        )}
        {line && <path d={line} fill="none" stroke="oklch(0.4 0.08 155)" strokeWidth={3} vectorEffect="non-scaling-stroke" />}
      </svg>
      {axis && axis.length > 0 && (
        <div className="mt-[6px] flex justify-between">
          {axis.map((a, i) => (
            <span key={i} className="text-[11px] text-[oklch(0.65_0.01_150)]">
              {a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Contrôles -------------------------------------------------------------------------

/** Groupe de pastilles (période, filtres). */
export function SegPills<T extends string>({
  options,
  value,
  onChange,
  className,
  size = "md",
}: {
  options: { key: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
  size?: "md" | "sm";
}) {
  return (
    <div className={cx("flex items-center gap-[2px] rounded-full bg-page p-1", className)}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cx(
            "rounded-full font-semibold transition-colors",
            size === "md" ? "px-4 py-[7px] text-[13px]" : "px-3 py-[6px] text-[12px]",
            value === o.key ? "bg-accent-strong text-white" : "text-muted hover:text-ink"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Pastilles de filtre (fond gris / vert actif), ex. filtres de conversation. */
export function FilterChips<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { key: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={cx("flex flex-wrap gap-[6px]", className)}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cx(
            "rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold",
            value === o.key ? "bg-accent text-white" : "bg-page text-[oklch(0.5_0.012_150)]"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Onglets soulignés (fiche boutique). */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { key: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex gap-2 overflow-x-auto border-b border-line">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={cx(
            "mr-5 whitespace-nowrap border-b-2 px-1 py-[10px] text-[13.5px] font-semibold",
            value === t.key ? "border-accent text-ink" : "border-transparent text-muted-2"
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = "primary",
  type = "button",
  disabled,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost" | "outline" | "danger";
  type?: "button" | "submit";
  disabled?: boolean;
  className?: string;
}) {
  const styles = {
    primary: "bg-accent text-white hover:bg-accent-strong",
    ghost: "bg-transparent text-accent hover:bg-accent-soft",
    outline: "border border-line bg-white text-ink hover:bg-page",
    danger: "bg-danger text-white",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cx("rounded-full px-[18px] py-[9px] text-[13px] font-bold transition-colors disabled:opacity-50", styles[variant], className)}
    >
      {children}
    </button>
  );
}

/** Menu déroulant (fermeture au clic extérieur). */
export function Dropdown({
  trigger,
  children,
  width = 200,
  align = "right",
  placement = "bottom",
}: {
  trigger: (open: boolean, toggle: () => void) => ReactNode;
  children: (close: () => void) => ReactNode;
  width?: number;
  align?: "left" | "right";
  /** « top » : le menu s'ouvre au-dessus du bouton (bas de la barre latérale). */
  placement?: "bottom" | "top";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  return (
    <div ref={ref} className="relative">
      {trigger(open, () => setOpen((o) => !o))}
      {open && (
        <div
          className={cx(
            "absolute z-30 rounded-card-sm bg-white p-2 shadow-menu",
            placement === "top" ? "bottom-[calc(100%+8px)]" : "top-[44px]",
            align === "right" ? "right-0" : "left-0"
          )}
          style={{ width }}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/** Panneau latéral droit (fiche produit, nouveau message…). */
export function Drawer({ open, onClose, width = 420, children }: { open: boolean; onClose: () => void; width?: number; children: ReactNode }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-[rgba(20,30,20,0.35)]" />
      <div
        role="dialog"
        className="fixed right-0 top-0 z-[41] flex h-[100dvh] flex-col gap-4 overflow-auto bg-white px-6 pb-[calc(26px+env(safe-area-inset-bottom))] pt-[calc(26px+env(safe-area-inset-top))] shadow-drawer"
        style={{ width: `min(${width}px, 100vw)` }}
      >
        {children}
      </div>
    </>
  );
}

export function DrawerHeader({ title, onClose }: { title: ReactNode; onClose: () => void }) {
  return (
    <div className="flex items-center gap-[10px]">
      <span className="flex-1 text-[16px] font-bold">{title}</span>
      <button type="button" aria-label="Fermer" onClick={onClose} className="text-[18px] text-muted-2">
        ×
      </button>
    </div>
  );
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return <div className="mb-[6px] text-[11.5px] font-bold uppercase text-muted-2">{children}</div>;
}

export const inputClass =
  "w-full rounded-[10px] border border-line bg-white px-3 py-[10px] text-[13px] font-[inherit] focus:border-accent";

// --- États ----------------------------------------------------------------------------

export function Loading({ label = "Chargement…", className }: { label?: string; className?: string }) {
  return <div className={cx("py-10 text-center text-[13px] text-muted", className)}>{label}</div>;
}

export function Empty({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("py-10 text-center text-[13px] text-muted", className)}>{children}</div>;
}

export function ErrorState({ error, className }: { error: unknown; className?: string }) {
  const msg = error instanceof Error ? error.message : "Une erreur est survenue";
  return <div className={cx("rounded-card-sm bg-danger-bg px-4 py-3 text-[13px] font-semibold text-danger", className)}>{msg}</div>;
}

/** Pastille ronde à initiales. */
export function Avatar({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cx("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-strong text-[12.5px] font-bold text-white", className)}>
      {text}
    </div>
  );
}
